import { useState, useEffect } from 'react';
import { LoginPage } from './components/LoginPage';
import { Dashboard } from './components/Dashboard';
import { fetchUsers, updateUser, deleteUser, fetchWebsiteStats, fetchFunnelStats } from './lib/api';
import { UserData, WebsiteDayStats, FunnelStats } from './types';

const SESSION_KEY = 'admin_password';

export default function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [users, setUsers] = useState<UserData[]>([]);
  const [websiteStats, setWebsiteStats] = useState<WebsiteDayStats[]>([]);
  const [funnelStats, setFunnelStats] = useState<FunnelStats>({ installs: 0, opens: 0 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastRefresh, setLastRefresh] = useState<Date | null>(null);

  useEffect(() => {
    const savedPassword = sessionStorage.getItem(SESSION_KEY);
    if (savedPassword) {
      loadData(savedPassword);
    }
  }, []);

  const handleLogin = async (password: string): Promise<boolean> => {
    try {
      const [usersResult, statsResult, funnelResult] = await Promise.all([
        fetchUsers(password),
        fetchWebsiteStats(password).catch(() => ({ stats: [] as WebsiteDayStats[] })),
        fetchFunnelStats(password).catch(() => ({ installs: 0, opens: 0 })),
      ]);
      setUsers(usersResult.users);
      setWebsiteStats(statsResult.stats);
      setFunnelStats(funnelResult);
      setIsAuthenticated(true);
      setLastRefresh(new Date());
      sessionStorage.setItem(SESSION_KEY, password);
      return true;
    } catch {
      return false;
    }
  };

  const loadData = async (password: string) => {
    setLoading(true);
    setError(null);
    try {
      const [usersResult, statsResult, funnelResult] = await Promise.all([
        fetchUsers(password),
        fetchWebsiteStats(password).catch(() => ({ stats: [] as WebsiteDayStats[] })),
        fetchFunnelStats(password).catch(() => ({ installs: 0, opens: 0 })),
      ]);
      setUsers(usersResult.users);
      setWebsiteStats(statsResult.stats);
      setFunnelStats(funnelResult);
      setIsAuthenticated(true);
      setLastRefresh(new Date());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load data');
      sessionStorage.removeItem(SESSION_KEY);
      setIsAuthenticated(false);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = () => {
    const password = sessionStorage.getItem(SESSION_KEY);
    if (password) {
      loadData(password);
    }
  };

  const handleUserUpdate = async (email: string, updates: Partial<UserData>) => {
    const password = sessionStorage.getItem(SESSION_KEY);
    if (!password) return;
    await updateUser(password, email, updates);
    await loadData(password);
  };

  const handleUserDelete = async (email: string) => {
    const password = sessionStorage.getItem(SESSION_KEY);
    if (!password) return;
    await deleteUser(password, email);
    await loadData(password);
  };

  const handleLogout = () => {
    sessionStorage.removeItem(SESSION_KEY);
    setIsAuthenticated(false);
    setUsers([]);
    setWebsiteStats([]);
    setFunnelStats({ installs: 0, opens: 0 });
  };

  if (!isAuthenticated) {
    return <LoginPage onLogin={handleLogin} />;
  }

  return (
    <Dashboard
      users={users}
      websiteStats={websiteStats}
      funnelStats={funnelStats}
      loading={loading}
      error={error}
      lastRefresh={lastRefresh}
      onRefresh={handleRefresh}
      onLogout={handleLogout}
      onUserUpdate={handleUserUpdate}
      onUserDelete={handleUserDelete}
    />
  );
}
